import { green50, green200, purple250, purple400 } from 'ext-ui/style/colors'
import type { InsightsBarChartData } from 'src/components/InsightsBarChart/InsightsBarChart'

export type ComparisonType = 'WOW' | 'MOM' | 'YOY' | null

/**
 * Formats ISO date string to short format (e.g., "Oct 12")
 */
function formatDateShort(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const day = date.getDate()
    return `${month} ${day}`
  } catch {
    return dateStr
  }
}

export interface PeriodDataPoint {
  date: string
  period: 'current' | 'prior'
  usdSpendCents?: number
  cadSpendCents?: number
  transactionCount?: number
  [key: string]: any
}

export interface AlignedPeriodData {
  currentDate: string
  priorDate?: string
  currentData: PeriodDataPoint
  priorData?: PeriodDataPoint
}

/**
 * Detects the comparison type based on period date ranges
 * Returns null if data doesn't have period comparison
 */
export function detectComparisonType(periodComparison?: {
  currentPeriodStart: string
  currentPeriodEnd: string
  priorPeriodStart: string
  priorPeriodEnd: string
}): ComparisonType {
  if (!periodComparison) {
    return null
  }

  const currentStart = new Date(periodComparison.currentPeriodStart)
  const currentEnd = new Date(periodComparison.currentPeriodEnd)
  const priorStart = new Date(periodComparison.priorPeriodStart)
  const priorEnd = new Date(periodComparison.priorPeriodEnd)

  // Calculate period lengths in days
  const currentDays = Math.ceil(
    (currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24),
  )
  const priorDays = Math.ceil(
    (priorEnd.getTime() - priorStart.getTime()) / (1000 * 60 * 60 * 24),
  )

  // Week-over-week: ~7 days
  if (currentDays <= 8 && priorDays <= 8) {
    return 'WOW'
  }

  // Month-over-month: ~28-31 days
  if (
    currentDays >= 28 &&
    currentDays <= 31 &&
    priorDays >= 28 &&
    priorDays <= 31
  ) {
    return 'MOM'
  }

  // Year-over-year: ~365 days
  if (currentDays >= 350 && priorDays >= 350) {
    return 'YOY'
  }

  // Default to MOM for other cases
  return 'MOM'
}

/**
 * Gets the day of week (0 = Sunday, 6 = Saturday) for a given date string
 */
export function getDayOfWeek(dateStr: string): number {
  return new Date(dateStr).getDay()
}

/**
 * Gets the day of the period (1-based) for a given date within a period
 */
export function getDayOfPeriod(dateStr: string, periodStart: string): number {
  const date = new Date(dateStr)
  const start = new Date(periodStart)
  const diffTime = date.getTime() - start.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays + 1 // 1-based
}

/**
 * Aligns prior period dates to current period dates based on comparison type
 */
export function alignPeriodDates(
  currentData: PeriodDataPoint[],
  priorData: PeriodDataPoint[],
  comparisonType: ComparisonType,
  periodComparison?: {
    currentPeriodStart: string
    currentPeriodEnd: string
    priorPeriodStart: string
    priorPeriodEnd: string
  },
): AlignedPeriodData[] {
  const aligned: AlignedPeriodData[] = []

  if (comparisonType === 'WOW') {
    // Week-over-week: align by day of week
    const priorByDayOfWeek = new Map<number, PeriodDataPoint[]>()
    priorData.forEach((item) => {
      const dayOfWeek = getDayOfWeek(item.date)
      if (!priorByDayOfWeek.has(dayOfWeek)) {
        priorByDayOfWeek.set(dayOfWeek, [])
      }
      priorByDayOfWeek.get(dayOfWeek)!.push(item)
    })

    currentData.forEach((currentItem) => {
      const dayOfWeek = getDayOfWeek(currentItem.date)
      const matchingPriorItems = priorByDayOfWeek.get(dayOfWeek) || []

      // Take the first matching prior item for this day of week
      const priorItem = matchingPriorItems[0]

      aligned.push({
        currentDate: currentItem.date,
        priorDate: priorItem?.date,
        currentData: currentItem,
        priorData: priorItem,
      })
    })
  } else {
    // Month-over-month or Year-over-year: align by relative day of period
    if (!periodComparison) {
      throw new Error(
        'Period comparison metadata required for MOM/YOY alignment',
      )
    }

    const priorByDayOfPeriod = new Map<number, PeriodDataPoint>()
    priorData.forEach((item) => {
      const dayOfPeriod = getDayOfPeriod(
        item.date,
        periodComparison.priorPeriodStart,
      )
      priorByDayOfPeriod.set(dayOfPeriod, item)
    })

    currentData.forEach((currentItem) => {
      const dayOfPeriod = getDayOfPeriod(
        currentItem.date,
        periodComparison.currentPeriodStart,
      )
      const priorItem = priorByDayOfPeriod.get(dayOfPeriod)

      aligned.push({
        currentDate: currentItem.date,
        priorDate: priorItem?.date,
        currentData: currentItem,
        priorData: priorItem,
      })
    })
  }

  return aligned
}

/**
 * Fills in missing dates with zero values for both current and prior periods
 */
export function fillMissingDatesForPeriods(
  alignedData: AlignedPeriodData[],
): AlignedPeriodData[] {
  if (alignedData.length === 0) {
    return alignedData
  }

  // Get date range from current period
  const currentDates = alignedData.map((item) => new Date(item.currentDate))
  const minDate = new Date(Math.min(...currentDates.map((d) => d.getTime())))
  const maxDate = new Date(Math.max(...currentDates.map((d) => d.getTime())))

  // Create a map of existing dates for quick lookup
  const dateMap = new Map(alignedData.map((item) => [item.currentDate, item]))

  // Get a template for zero values from the first item
  const firstCurrentData = alignedData[0].currentData
  const firstPriorData = alignedData[0].priorData

  const createZeroData = (
    date: string,
    period: 'current' | 'prior',
  ): PeriodDataPoint => {
    const template = period === 'current' ? firstCurrentData : firstPriorData
    const zeroItem: PeriodDataPoint = { date, period }

    if (template) {
      Object.keys(template).forEach((key) => {
        if (typeof template[key] === 'number' && key !== 'transactionCount') {
          zeroItem[key] = 0
        }
      })
    }

    return zeroItem
  }

  // Generate all dates in the range
  const allDates: AlignedPeriodData[] = []
  const currentDate = new Date(minDate)

  while (currentDate <= maxDate) {
    const dateStr = currentDate.toISOString().split('T')[0]

    if (dateMap.has(dateStr)) {
      allDates.push(dateMap.get(dateStr)!)
    } else {
      // Create zero entries for missing dates
      allDates.push({
        currentDate: dateStr,
        priorDate: undefined,
        currentData: createZeroData(dateStr, 'current'),
        priorData: undefined,
      })
    }

    currentDate.setDate(currentDate.getDate() + 1)
  }

  return allDates
}

/**
 * Transforms aligned period data into grouped bar chart format
 * Groups by currency first, then by period (prior, current)
 */
export function transformPeriodDataToBarChart(
  alignedData: AlignedPeriodData[],
): {
  chartData: InsightsBarChartData[]
  barKeys: string[]
  hasCadData: boolean
  hasUsdData: boolean
} {
  const chartData: InsightsBarChartData[] = []
  let hasAnyUsdData = false
  let hasAnyCadData = false

  alignedData.forEach((item) => {
    const label = formatDateShort(item.currentDate)

    // Extract USD and CAD values from both periods
    const priorUsdSpend = item.priorData?.usdSpendCents ?? 0
    const currentUsdSpend = item.currentData.usdSpendCents ?? 0
    const priorCadSpend = item.priorData?.cadSpendCents ?? 0
    const currentCadSpend = item.currentData.cadSpendCents ?? 0

    // Track if we have any non-zero data for each currency
    if (priorUsdSpend > 0 || currentUsdSpend > 0) {
      hasAnyUsdData = true
    }
    if (priorCadSpend > 0 || currentCadSpend > 0) {
      hasAnyCadData = true
    }

    const dataPoint: InsightsBarChartData = {
      label,
      priorUsdSpend: priorUsdSpend / 100,
      currentUsdSpend: currentUsdSpend / 100,
      priorCadSpend: priorCadSpend / 100,
      currentCadSpend: currentCadSpend / 100,
      // Store dates for tooltip display
      currentDate: item.currentDate,
      priorDate: item.priorDate,
    }

    chartData.push(dataPoint)
  })

  // Determine which bar keys to include based on data presence
  const barKeys: string[] = []

  if (hasAnyUsdData) {
    barKeys.push('priorUsdSpend', 'currentUsdSpend')
  }

  if (hasAnyCadData) {
    barKeys.push('priorCadSpend', 'currentCadSpend')
  }

  return {
    chartData,
    barKeys,
    hasCadData: hasAnyCadData,
    hasUsdData: hasAnyUsdData,
  }
}

/**
 * Main mapper function for period comparison data
 */
export function mapPeriodComparisonToBarChart(
  data: PeriodDataPoint[],
  comparisonType: ComparisonType,
  periodComparison?: {
    currentPeriodStart: string
    currentPeriodEnd: string
    priorPeriodStart: string
    priorPeriodEnd: string
  },
): {
  chartData: InsightsBarChartData[]
  barKeys: string[]
  barColors: string[]
} {
  // Separate current and prior period data
  const currentData = data.filter((item) => item.period === 'current')
  const priorData = data.filter((item) => item.period === 'prior')

  // Sort both periods by date
  currentData.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )
  priorData.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Align periods based on comparison type
  const aligned = alignPeriodDates(
    currentData,
    priorData,
    comparisonType,
    periodComparison,
  )

  // Fill missing dates with zeros
  const filledData = fillMissingDatesForPeriods(aligned)

  // Transform to bar chart format
  const { chartData, barKeys, hasCadData, hasUsdData } =
    transformPeriodDataToBarChart(filledData)

  // Determine bar colors based on which currencies are present
  // Order: prior USD, current USD, prior CAD, current CAD
  const barColors: string[] = []

  if (hasUsdData) {
    barColors.push(purple250)
    barColors.push(purple400)
  }

  if (hasCadData) {
    barColors.push(green50)
    barColors.push(green200)
  }

  return {
    chartData,
    barKeys,
    barColors,
  }
}
